import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Spinner, Row, Col, Badge, Form, Button } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import 'chartjs-plugin-zoom';
import dateformat from 'utils/dateformat';
import Api from 'services/Api';

const Form4 = ({ serial }) => {
  const [telemetry, setTelemetry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [show, setShow] = useState({ cpu: true, ram: true, temp: true, lat: true, wifi: true, disk: true, uptime: true });
  const chartRef = useRef(null);
  const MAX_POINTS = 500; // limite máximo de pontos plotados por série
  const [useAgg, setUseAgg] = useState(true); // visualização filtrada (agregada) por padrão

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await Api.get(`/api/v1/telemetria-equipments`, { params: { serial } });
        if (!mounted) return;
        // Garante ordenação por timestamp ascendente
        const list = Array.isArray(data)
          ? [...data].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
          : [];
        setTelemetry(list);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar telemetria:', err);
        if (mounted) setError('Não foi possível carregar a telemetria');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [serial]);

  const latest = telemetry.length ? telemetry[telemetry.length - 1] : null;

  const wifiQualityFromDbm = (dbm) => {
    if (typeof dbm !== 'number') return null;
    const q = Math.max(0, Math.min(100, 2 * (dbm + 100)));
    return Math.round(q);
  };

  const parseSizeGB = (val) => {
    if (val == null) return null;
    const m = String(val).trim().toUpperCase().match(/([0-9]*\.?[0-9]+)\s*([GMK]?B?)/);
    if (!m) return null;
    let num = parseFloat(m[1]);
    const unit = m[2];
    if (unit === 'M' || unit === 'MB') num = num / 1024;
    if (unit === 'K' || unit === 'KB') num = num / 1024 / 1024;
    return num; // GB
  };

  const formatUptime = (minutes) => {
    if (typeof minutes !== 'number' || minutes < 0) return '--';
    const d = Math.floor(minutes / (60 * 24));
    const h = Math.floor((minutes % (60 * 24)) / 60);
    const m = Math.floor(minutes % 60);
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0 || d > 0) parts.push(`${h}h`);
    parts.push(`${m}m`);
    return parts.join(' ');
  };

  const asPercent = (part, total) =>
    typeof part === 'number' && typeof total === 'number' && total > 0
      ? Math.round((part / total) * 1000) / 10
      : null;

  // Helpers de agregação para reduzir a quantidade de pontos no gráfico
  const avg = (arr) => {
    const nums = arr.filter((v) => typeof v === 'number' && !isNaN(v));
    if (!nums.length) return null;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  };

  const mode = (arr) => {
    const values = arr.filter(Boolean);
    if (!values.length) return null;
    const freq = values.reduce((acc, v) => {
      acc[v] = (acc[v] || 0) + 1;
      return acc;
    }, {});
    return Object.keys(freq).sort((a, b) => freq[b] - freq[a])[0];
  };

  const downsampleTelemetry = (rows, maxPoints) => {
    if (!Array.isArray(rows) || rows.length <= maxPoints) return rows || [];
    const chunkSize = Math.ceil(rows.length / maxPoints);
    const out = [];
    for (let i = 0; i < rows.length; i += chunkSize) {
      const chunk = rows.slice(i, i + chunkSize);
      const last = chunk[chunk.length - 1] || chunk[0] || {};

      const cpu_usage = avg(chunk.map((x) => (typeof x.cpu_usage === 'number' ? x.cpu_usage : null)));
      const cpu_temp = avg(chunk.map((x) => (typeof x.cpu_temp === 'number' ? x.cpu_temp : null)));
      const tcp_latency_ms = avg(chunk.map((x) => (typeof x.tcp_latency_ms === 'number' ? x.tcp_latency_ms : null)));
      const wifi_signal = avg(chunk.map((x) => (typeof x.wifi_signal === 'number' ? x.wifi_signal : null)));
      const uptime_minutes = avg(chunk.map((x) => (typeof x.uptime_minutes === 'number' ? x.uptime_minutes : null)));
      const network_type = mode(chunk.map((x) => x.network_type));

      // RAM
      const ram_used = avg(chunk.map((x) => (x && x.ram && typeof x.ram.used === 'number' ? x.ram.used : null)));
      const ram_total = avg(chunk.map((x) => (x && x.ram && typeof x.ram.total === 'number' ? x.ram.total : null)));
      const ram = ram_used != null || ram_total != null ? { used: ram_used ?? null, total: ram_total ?? null } : null;

      // Disco (formatar em GB com sufixo para manter compatibilidade com parseSizeGB)
      const disk_free_gb = avg(chunk.map((x) => (x && x.disk ? parseSizeGB(x.disk.free) : null)));
      const disk_total_gb = avg(chunk.map((x) => (x && x.disk ? parseSizeGB(x.disk.total) : null)));
      const disk =
        disk_free_gb != null || disk_total_gb != null
          ? {
              free: disk_free_gb != null ? `${disk_free_gb.toFixed(1)} GB` : null,
              total: disk_total_gb != null ? `${disk_total_gb.toFixed(1)} GB` : null,
            }
          : null;

      out.push({
        timestamp: last.timestamp,
        cpu_usage,
        cpu_temp,
        tcp_latency_ms,
        wifi_signal,
        uptime_minutes,
        network_type,
        ram,
        disk,
      });
    }
    return out;
  };

  // Telemetria agregada para o gráfico (mantém a original para o resumo "latest")
  const telemetryAgg = useMemo(() => downsampleTelemetry(telemetry, MAX_POINTS), [telemetry]);

  const { chartOptions } = useMemo(() => {
    const rows = useAgg ? telemetryAgg : telemetry; // conjunto de dados conforme modo
    const tooltipRows = rows; // usar as mesmas linhas no tooltip
    const labels = rows.map((t) =>
      t.timestamp ? dateformat(t.timestamp, "UTC:dd/mm/yy' 'HH:MM") : ''
    );

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function (tooltipItem, data) {
            const ds = data.datasets[tooltipItem.datasetIndex] || {};
            const label = ds.label || '';
            const val = tooltipItem.yLabel;
            if (label.includes('CPU %') || label.includes('RAM %') || label.includes('Wi‑Fi') || label.includes('Disco %'))
              return `${label}: ${val !== undefined ? val : '--'}%`;
            if (label.includes('Temp')) return `${label}: ${val !== undefined ? val : '--'} °C`;
            if (label.includes('Latência')) return `${label}: ${val !== undefined ? val : '--'} ms`;
            if (label.includes('Uptime')) return `${label}: ${val !== undefined ? val : '--'} h`;
            return `${label}: ${val !== undefined ? val : '--'}`;
          },
          afterBody: function (tooltipItems) {
            if (!tooltipItems || !tooltipItems.length) return '';
            const idx = tooltipItems[0].index;
            const row = tooltipRows[idx];
            if (!row) return '';
            const parts = [];
            if (row.ram && typeof row.ram.used === 'number' && typeof row.ram.total === 'number') {
              parts.push(`RAM: ${row.ram.used} / ${row.ram.total} MB`);
            }
            if (typeof row.wifi_signal === 'number') {
              parts.push(`Wi‑Fi: ${row.wifi_signal} dBm`);
            }
            if (row.network_type) {
              parts.push(`Rede: ${row.network_type}`);
            }
            if (row.disk && row.disk.free && row.disk.total) {
              parts.push(`Disco: ${row.disk.free} livre / ${row.disk.total}`);
            }
            if (typeof row.uptime_minutes === 'number') {
              parts.push(`Uptime: ${formatUptime(row.uptime_minutes)}`);
            }
            return parts.join('\n');
          }
        }
      },
      hover: { mode: 'nearest', intersect: false },
      legend: { position: 'top' },
      title: { display: true, text: 'Telemetria do Equipamento' },
      scales: {
        xAxes: [
          {
            ticks: { maxRotation: 0, autoSkip: true, autoSkipPadding: 8 },
            gridLines: { color: '#eef1f5' }
          }
        ],
        yAxes: [
          {
            id: 'y_percent',
            type: 'linear',
            position: 'left',
            ticks: { suggestedMin: 0, suggestedMax: 100 },
            scaleLabel: { display: true, labelString: 'Percentual (%)' }
          },
          {
            id: 'y_temp',
            type: 'linear',
            position: 'right',
            gridLines: { drawOnChartArea: false },
            ticks: { suggestedMin: 0, suggestedMax: 100 },
            scaleLabel: { display: true, labelString: 'Temperatura (°C)' }
          },
          {
            id: 'y_latency',
            type: 'linear',
            position: 'right',
            gridLines: { drawOnChartArea: false },
            ticks: { suggestedMin: 0 },
            scaleLabel: { display: true, labelString: 'Latência (ms)' }
          },
          {
            id: 'y_uptime',
            type: 'linear',
            position: 'right',
            gridLines: { drawOnChartArea: false },
            ticks: { suggestedMin: 0 },
            scaleLabel: { display: true, labelString: 'Uptime (h)' }
          }
        ]
      },
      pan: { enabled: true, mode: 'x', speed: 10, threshold: 10 },
      zoom: { enabled: true, mode: 'x', speed: 0.1, sensitivity: 3 }
    };

    return { chartOptions };
  }, [telemetryAgg, telemetry, useAgg]);

  const buildChartData = (canvas) => {
    const ctx = canvas.getContext('2d');
    const base = useAgg ? telemetryAgg : telemetry;
    const labels = base.map((t) =>
      t.timestamp ? dateformat(t.timestamp, "UTC:dd/mm/yy' 'HH:MM") : ''
    );

    const cpuUsage = base.map((t) =>
      typeof t.cpu_usage === 'number' ? Math.round(t.cpu_usage * 10) / 10 : null
    );
    const ramPercent = base.map((t) =>
      t.ram ? asPercent(t.ram.used, t.ram.total) : null
    );
    const cpuTemp = base.map((t) =>
      typeof t.cpu_temp === 'number' ? Math.round(t.cpu_temp * 10) / 10 : null
    );
    const latency = base.map((t) =>
      typeof t.tcp_latency_ms === 'number' ? Math.round(t.tcp_latency_ms) : null
    );
    const wifiQuality = base.map((t) => wifiQualityFromDbm(t.wifi_signal));
    const diskPercent = base.map((t) => {
      const free = t && t.disk ? parseSizeGB(t.disk.free) : null;
      const total = t && t.disk ? parseSizeGB(t.disk.total) : null;
      if (typeof free !== 'number' || typeof total !== 'number' || total === 0) return null;
      return Math.round((1 - free / total) * 1000) / 10; // % usado
    });
    const uptimeHours = base.map((t) =>
      typeof t.uptime_minutes === 'number' ? Math.round((t.uptime_minutes / 60) * 10) / 10 : null
    );

    // Gradientes sutis
    const mkGrad = (color) => {
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
      g.addColorStop(0, color.replace('1)', '0.25)'));
      g.addColorStop(1, color.replace('1)', '0.02)'));
      return g;
    };

    const datasets = [];
    if (show.cpu) datasets.push({
      label: 'CPU %',
      data: cpuUsage,
      borderColor: 'rgba(255, 99, 132, 1)',
      backgroundColor: mkGrad('rgba(255, 99, 132, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_percent',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
    });
    if (show.ram) datasets.push({
      label: 'RAM %',
      data: ramPercent,
      borderColor: 'rgba(54, 162, 235, 1)',
      backgroundColor: mkGrad('rgba(54, 162, 235, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_percent',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
    });
    if (show.temp) datasets.push({
      label: 'Temp CPU (°C)',
      data: cpuTemp,
      borderColor: 'rgba(255, 159, 64, 1)',
      backgroundColor: mkGrad('rgba(255, 159, 64, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_temp',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
    });
    if (show.lat) datasets.push({
      label: 'Latência (ms)',
      data: latency,
      borderColor: 'rgba(153, 102, 255, 1)',
      backgroundColor: mkGrad('rgba(153, 102, 255, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_latency',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderDash: [6, 4],
    });
    if (show.wifi) datasets.push({
      label: 'Wi‑Fi (%)',
      data: wifiQuality,
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: mkGrad('rgba(75, 192, 192, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_percent',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
    });
    if (show.disk) datasets.push({
      label: 'Disco %',
      data: diskPercent,
      borderColor: 'rgba(255, 205, 86, 1)',
      backgroundColor: mkGrad('rgba(255, 205, 86, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_percent',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
    });
    if (show.uptime) datasets.push({
      label: 'Uptime (h)',
      data: uptimeHours,
      borderColor: 'rgba(201, 203, 207, 1)',
      backgroundColor: mkGrad('rgba(201, 203, 207, 1)'),
      lineTension: 0.25,
      yAxisID: 'y_uptime',
      spanGaps: true,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderDash: [2, 3],
    });

    return { labels, datasets };
  };

  return (
    <Card className="mb-3 mt-3">
      <Card.Header>
        <h5 className='mt-2 mb-2'>Telemetria</h5>
      </Card.Header>
      <Card.Body>
        {/* Resumo atual */}
        {!loading && !error && latest && (
          <Row className="mb-3 g-2">
            <Col sm="auto">
              <Badge bg={(() => {
                const v = typeof latest.cpu_usage === 'number' ? latest.cpu_usage : null;
                if (v == null) return 'secondary';
                if (v < 70) return 'success';
                if (v < 90) return 'warning';
                return 'danger';
              })()}>
                CPU: {latest.cpu_usage != null ? `${Math.round(latest.cpu_usage * 10) / 10}%` : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg={(() => {
                const v = latest.ram ? asPercent(latest.ram.used, latest.ram.total) : null;
                if (v == null) return 'secondary';
                if (v < 70) return 'success';
                if (v < 90) return 'warning';
                return 'danger';
              })()}>
                RAM: {latest.ram ? `${asPercent(latest.ram.used, latest.ram.total)}%` : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg={(() => {
                const v = typeof latest.cpu_temp === 'number' ? latest.cpu_temp : null;
                if (v == null) return 'secondary';
                if (v < 60) return 'success';
                if (v < 80) return 'warning';
                return 'danger';
              })()}>
                Temp: {latest.cpu_temp != null ? `${Math.round(latest.cpu_temp * 10) / 10} °C` : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg={(() => {
                const v = typeof latest.tcp_latency_ms === 'number' ? latest.tcp_latency_ms : null;
                if (v == null) return 'secondary';
                if (v < 150) return 'success';
                if (v < 500) return 'warning';
                return 'danger';
              })()}>
                Latência: {latest.tcp_latency_ms != null ? `${Math.round(latest.tcp_latency_ms)} ms` : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg={(() => {
                const q = wifiQualityFromDbm(latest.wifi_signal);
                if (q == null) return 'secondary';
                if (q > 70) return 'success';
                if (q > 40) return 'warning';
                return 'danger';
              })()}>
                Wi‑Fi: {latest.wifi_signal != null ? `${wifiQualityFromDbm(latest.wifi_signal)}% (${latest.wifi_signal} dBm)` : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg={(() => {
                const free = latest.disk ? parseSizeGB(latest.disk.free) : null;
                const total = latest.disk ? parseSizeGB(latest.disk.total) : null;
                if (free == null || total == null || total === 0) return 'secondary';
                const usedPct = Math.round((1 - free / total) * 100);
                if (usedPct < 70) return 'success';
                if (usedPct < 90) return 'warning';
                return 'danger';
              })()}>
                Disco: {latest.disk && latest.disk.free && latest.disk.total
                  ? `${latest.disk.free} livre / ${latest.disk.total}`
                  : '--'}
              </Badge>
            </Col>
            <Col sm="auto">
              <Badge bg="info">
                Uptime: {formatUptime(latest.uptime_minutes)}
              </Badge>
            </Col>
          </Row>
        )}

        {/* Controles de visualização */}
        {!loading && !error && telemetry.length > 0 && (
          <Row className="align-items-center mb-2 g-2">
            <Col xs={12} md="auto">
              <Form.Check inline label="CPU" type="checkbox" id="chk-cpu" checked={show.cpu} onChange={(e) => setShow((s) => ({ ...s, cpu: e.target.checked }))} />
              <Form.Check inline label="RAM" type="checkbox" id="chk-ram" checked={show.ram} onChange={(e) => setShow((s) => ({ ...s, ram: e.target.checked }))} />
              <Form.Check inline label="Temp" type="checkbox" id="chk-temp" checked={show.temp} onChange={(e) => setShow((s) => ({ ...s, temp: e.target.checked }))} />
              <Form.Check inline label="Latência" type="checkbox" id="chk-lat" checked={show.lat} onChange={(e) => setShow((s) => ({ ...s, lat: e.target.checked }))} />
              <Form.Check inline label="Wi‑Fi" type="checkbox" id="chk-wifi" checked={show.wifi} onChange={(e) => setShow((s) => ({ ...s, wifi: e.target.checked }))} />
              <Form.Check inline label="Disco" type="checkbox" id="chk-disk" checked={show.disk} onChange={(e) => setShow((s) => ({ ...s, disk: e.target.checked }))} />
              <Form.Check inline label="Uptime" type="checkbox" id="chk-uptime" checked={show.uptime} onChange={(e) => setShow((s) => ({ ...s, uptime: e.target.checked }))} />
            </Col>
            <Col xs="auto">
              <div className="btn-group" role="group" aria-label="Modo de visualização">
                <Button size="sm" variant={useAgg ? 'primary' : 'outline-primary'} onClick={() => setUseAgg(true)}>
                  Filtrado
                </Button>
                <Button size="sm" variant={!useAgg ? 'primary' : 'outline-primary'} onClick={() => setUseAgg(false)}>
                  Todos
                </Button>
              </div>
            </Col>
            <Col xs="auto" className="ms-auto">
              <Button variant="outline-secondary" size="sm" onClick={() => {
                const inst = chartRef.current && chartRef.current.chartInstance;
                if (inst && inst.resetZoom) inst.resetZoom();
              }}>Resetar Zoom</Button>
            </Col>
          </Row>
        )}
        {loading && (
          <div className="d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            Carregando telemetria...
          </div>
        )}
        {!loading && error && <div className="text-danger">{error}</div>}
        {!loading && !error && telemetry.length === 0 && (
          <div className="text-muted">Nenhum dado de telemetria encontrado.</div>
        )}
        {!loading && !error && telemetry.length > 0 && (
          <div style={{ height: 360 }}>
            <Line ref={chartRef} data={buildChartData} options={chartOptions} />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default Form4;
